import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatBRL } from "../api";
import { VultLogo } from "../components/Logo";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("pedido");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    localStorage.removeItem("vult_cart");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (orderId) {
            const o = await api.get(`/orders/track/${orderId}`);
            setOrder(o.data);
          }
          return;
        }
      } catch (e) {
        console.error(e);
      }
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    if (sessionId) poll();
    else setStatus("error");
    return () => clearTimeout(timer);
  }, [sessionId, orderId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] px-4">
      <motion.div
        data-testid="vult-payment-success-page"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-[8px_8px_0_#0F172A]"
      >
        <div className="flex justify-center"><VultLogo /></div>
        {status === "checking" && (
          <div className="mt-8 flex flex-col items-center">
            <Loader2 size={48} className="animate-spin text-vult-blue" />
            <h1 className="mt-4 font-display text-2xl font-black uppercase text-slate-900">Confirmando pagamento...</h1>
            <p className="mt-1 text-sm text-slate-500">Aguarde um instante.</p>
          </div>
        )}
        {status === "paid" && (
          <div className="mt-8 flex flex-col items-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}>
              <CheckCircle2 size={64} className="text-emerald-500" />
            </motion.div>
            <h1 className="mt-4 font-display text-3xl font-black uppercase text-slate-900">Pagamento aprovado!</h1>
            <p className="mt-2 text-sm text-slate-600">Seu pedido foi confirmado e os detalhes foram enviados para o seu e-mail.</p>
            <p data-testid="vult-payment-prazo" className="mt-3 rounded-full bg-vult-lime px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900">Prazo estimado: 20 dias até o destino final</p>
            {order && (
              <div className="mt-6 w-full space-y-2 rounded-2xl bg-[#F8F9FA] p-5 font-mono text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Pedido</span><span data-testid="vult-payment-order-id" className="font-bold">{order.id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Rastreio</span><span data-testid="vult-payment-tracking" className="font-bold text-vult-blue">{order.tracking_code}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total pago</span><span className="font-bold">{formatBRL(order.total)}</span></div>
              </div>
            )}
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div className="mt-8 flex flex-col items-center">
            <AlertTriangle size={56} className="text-amber-500" />
            <h1 className="mt-4 font-display text-2xl font-black uppercase text-slate-900">Quase lá</h1>
            <p className="mt-2 text-sm text-slate-600">Seu pagamento está sendo processado. Assim que confirmar, o pedido {orderId ? <strong>{orderId}</strong> : ""} será atualizado e você receberá o e-mail.</p>
          </div>
        )}
        <Link
          data-testid="vult-payment-back-home"
          to="/"
          className="mt-8 block w-full rounded-full bg-slate-900 py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-vult-red hover:text-slate-900"
        >
          Voltar à loja
        </Link>
      </motion.div>
    </div>
  );
}
